import React, { useRef, useState, useEffect, useCallback } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Download, Loader2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import SocialAdvertCanvas, { FORMATS } from "../components/advert/SocialAdvertCanvas";
import { socialAdvert } from "../lib/defaultAdvert";

const AdvertStudio = () => {
  const [format, setFormat] = useState("square");
  const [busy, setBusy] = useState(false);
  const [scale, setScale] = useState(0.4);
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const fmt = FORMATS[format];

  useEffect(() => {
    const compute = () => {
      const w = wrapperRef.current?.clientWidth || fmt.w;
      const h = window.innerHeight - 200;
      const sw = (w - 16) / fmt.w;
      const sh = h / fmt.h;
      setScale(Math.min(1, sw, sh));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [fmt.w, fmt.h]);

  const handleDownload = useCallback(
    async (targetFormat) => {
      const f = targetFormat || format;
      const node = canvasRef.current;
      if (!node) return;
      setBusy(true);
      try {
        // If downloading a different format, temporarily switch to it
        if (targetFormat && targetFormat !== format) {
          setFormat(targetFormat);
          await new Promise((r) => setTimeout(r, 400));
        }
        const dataUrl = await toPng(canvasRef.current, {
          width: FORMATS[f].w,
          height: FORMATS[f].h,
          pixelRatio: 2,
          cacheBust: true,
          style: { transform: "scale(1)", transformOrigin: "top left" },
        });
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `messy-pups-advert-${f}-${FORMATS[f].w}x${FORMATS[f].h}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success(`${FORMATS[f].label} downloaded`);
      } catch (e) {
        console.error(e);
        toast.error("Could not generate PNG");
      } finally {
        setBusy(false);
      }
    },
    [format]
  );

  const downloadAll = useCallback(async () => {
    setBusy(true);
    try {
      for (const key of Object.keys(FORMATS)) {
        setFormat(key);
        await new Promise((r) => setTimeout(r, 500));
        const dataUrl = await toPng(canvasRef.current, {
          width: FORMATS[key].w,
          height: FORMATS[key].h,
          pixelRatio: 2,
          cacheBust: true,
          style: { transform: "scale(1)", transformOrigin: "top left" },
        });
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `messy-pups-advert-${key}-${FORMATS[key].w}x${FORMATS[key].h}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        await new Promise((r) => setTimeout(r, 250));
      }
      toast.success("All four formats downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Batch download failed — try one at a time");
    } finally {
      setBusy(false);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F1] text-[#0C2A20]" data-testid="advert-studio-page">
      <header className="sticky top-0 z-30 bg-[#FAF7F1]/90 backdrop-blur border-b border-stone-200">
        <div className="max-w-[1440px] mx-auto px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-stone-500 hover:text-[#114232] flex items-center gap-1 text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to site
            </Link>
            <div className="h-5 w-px bg-stone-300" />
            <img src="/brand/logo-circle.png" alt="" className="w-9 h-9 rounded-full" />
            <div>
              <h1 className="text-lg text-[#114232] leading-none" style={{ fontFamily: "Chewy, system-ui" }}>
                Social advert pack
              </h1>
              <p className="text-[11px] text-stone-500 tracking-wider uppercase">
                Four formats · ready for FB · Insta · Stories · Link previews
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={downloadAll}
              disabled={busy}
              data-testid="download-all-btn"
              className="border-[#94A596] text-[#114232]"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
              Download all 4
            </Button>
            <Button
              size="sm"
              onClick={() => handleDownload()}
              disabled={busy}
              data-testid="download-current-btn"
              className="bg-[#E07856] hover:opacity-90 text-white"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
              Download this one
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-5 py-6 grid gap-6 lg:grid-cols-[280px_1fr]">
        {/* Format picker */}
        <aside className="space-y-2" data-testid="format-picker">
          <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Pick a format</div>
          {Object.entries(FORMATS).map(([key, f]) => {
            const active = key === format;
            return (
              <button
                key={key}
                onClick={() => setFormat(key)}
                data-testid={`format-${key}`}
                className={`w-full text-left rounded-xl p-4 border transition-colors ${
                  active
                    ? "bg-[#114232] text-[#FAF7F1] border-[#114232]"
                    : "bg-white text-[#114232] border-stone-200 hover:border-[#94A596]"
                }`}
              >
                <div style={{ fontFamily: "Chewy, system-ui" }} className="text-xl leading-none">
                  {f.label.split(" ")[0]}
                </div>
                <div className={`text-xs mt-1 ${active ? "text-[#B7C3BB]" : "text-stone-500"}`}>
                  {f.w} × {f.h} · {f.use}
                </div>
              </button>
            );
          })}

          <div className="mt-6 rounded-xl border border-stone-200 bg-white p-4 text-xs text-stone-600 leading-relaxed">
            <div className="font-semibold text-[#114232] mb-1">How to use</div>
            Download the format that matches where you're posting. PNG is high-resolution (2× pixel ratio) so it looks sharp on any device. Copy the file, upload to Facebook / Instagram / eBay / LinkedIn / your printer.
          </div>
        </aside>

        {/* Preview */}
        <main data-testid="advert-preview">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-stone-500">Preview</div>
              <div className="text-lg text-[#114232]" style={{ fontFamily: "Chewy, system-ui" }}>
                {fmt.label} <span className="text-stone-400 text-sm">· {fmt.use}</span>
              </div>
            </div>
            <Button
              size="lg"
              onClick={() => handleDownload()}
              disabled={busy}
              data-testid="download-primary-btn"
              className="bg-[#E07856] hover:opacity-90 text-white shadow-lg"
              style={{ boxShadow: "0 10px 24px rgba(224,120,86,0.35)" }}
            >
              {busy ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Download className="w-5 h-5 mr-2" />
              )}
              Download {fmt.label.split(" ")[0]} PNG
            </Button>
          </div>
          <div
            ref={wrapperRef}
            className="bg-stone-100 border border-stone-200 rounded-xl overflow-auto flex items-start justify-center p-4"
            style={{ minHeight: 400 }}
          >
            <div
              style={{
                width: fmt.w * scale,
                height: fmt.h * scale,
                flexShrink: 0,
              }}
            >
              <SocialAdvertCanvas
                ref={canvasRef}
                format={format}
                advert={socialAdvert}
                scale={scale}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdvertStudio;
